import React from 'react';

import CalculatorBase from './calculator-base';
import CalculatorInput from './calculator-input';
import CalculatorOutput from './calculator-output';
import { PieChart } from '../chart';

import { calculateHomeOutputs } from '../../utilities/calculations';

const renderBasicInputs = (state, processInput) => {
    const { zipCode, costOfAsset, downPayment, interestRate, loanPeriod, propertyTax, insurance, hoa, pmi } = state;

    return (
        <div>
            <CalculatorInput
                label="Zip Code"
                id="zipCode"
                defaultValue={zipCode}
                format="int"
                processInput={processInput}
                showSlider={false}
                controlledId="propertyTax"
            />
            <CalculatorInput
                id="costOfAsset"
                label="Cost of Home (Home Value)"
                defaultValue={costOfAsset}
                processInput={processInput}
            />
            <CalculatorInput
                id="downPayment"
                label="Down Payment"
                defaultValue={downPayment}
                processInput={processInput}
                maxSliderValue={costOfAsset}
            />
            <CalculatorInput
                id="interestRate"
                label="Interest Rate"
                defaultValue={interestRate}
                format="percent"
                processInput={processInput}
            />
            <CalculatorInput
                id="loanPeriod"
                label="Loan Period in Years"
                defaultValue={loanPeriod}
                format="int"
                processInput={processInput}
                maxSliderValue={50}
            />
	    <CalculatorInput
                label="Property Tax"
                id="propertyTax"
                defaultValue={propertyTax}
                format="percent"
                processInput={processInput}
            />
	    <CalculatorInput
                label="Insurance Monthly"
                id="insurance"
                defaultValue={insurance}
                processInput={processInput}
            />
	    <CalculatorInput
                label="Private Mortgage Insurance Monthly"
                id="pmi"
                defaultValue={pmi}
                processInput={processInput}
            />
	    <CalculatorInput
                label="Home Owner's Association Fees Monthly"
                id="hoa"
                defaultValue={hoa}
                processInput={processInput}
            />
        </div>
    );
};

const renderBasicOutputs = (state) => {
    const {
        monthlyPrincipal,
        monthlyInterest,
        monthlyTax,
        insurance,
        pmi,
        hoa,
        totalInterest,
        totalPrincipalAndInterest,
    } = calculateHomeOutputs(state);

    const { downPayment, loanPeriod } = state;

    const monthlyMortgage = monthlyInterest + monthlyPrincipal;
    const monthlyTaxesAndFees = monthlyTax + insurance + pmi + hoa;
    const totalMonthly = monthlyMortgage + monthlyTaxesAndFees;

    const lifetimePrincipal = totalPrincipalAndInterest - totalInterest;
    const lifetimeTaxesAndFees = monthlyTaxesAndFees * 12 * loanPeriod;
    const lifetimeBasicAll = totalPrincipalAndInterest + downPayment + lifetimeTaxesAndFees;

    return [
        <CalculatorOutput label="Payments & Expenses (monthly)" key="0" headingLevel={1} />,
        <CalculatorOutput label="Mortgage Payment" key="01" headingLevel={1} indentLevel={1} />,
        <CalculatorOutput label="Principal" value={monthlyPrincipal} key="1" indentLevel={2} />,
        <CalculatorOutput label="Interest" value={monthlyInterest} key="2" indentLevel={2} />,
        <CalculatorOutput label="Total Mortgage Payments" value={monthlyMortgage} key="001" indentLevel={1} headingLevel={1} />,
        <br />,
        <CalculatorOutput label="Taxes & Fees" indentLevel={1} headingLevel={1} />,
        <CalculatorOutput label="Property Tax" value={monthlyTax} key="3" indentLevel={2} />,
        <CalculatorOutput label="Insurance" value={insurance} key="4" indentLevel={2} />,
        <CalculatorOutput label="Private Mortage Insurance" value={pmi} key="5" indentLevel={2} />,
        <CalculatorOutput label="Home Owner's Association" value={hoa} key="6" indentLevel={2} />,
        <CalculatorOutput label="Total Taxes & Fees" value={monthlyTaxesAndFees} key="1001" indentLevel={1} headingLevel={1} />,
        <br />,
        <CalculatorOutput label="Monthly Expenses Total" value={totalMonthly} headingLevel={2} />,
        <br />,
        <CalculatorOutput label="Lifetime Totals" headingLevel={1} />,
        <CalculatorOutput label="Down Payment" value={downPayment} indentLevel={1} />,
        <CalculatorOutput label="Interest" value={totalInterest} key="7" indentLevel={1} />,
        <CalculatorOutput label="Principal" value={lifetimePrincipal} key="8" indentLevel={1} />,
        <CalculatorOutput label="Taxes & Fees" value={lifetimeTaxesAndFees} indentLevel={1} />,
        <CalculatorOutput label="Total Payments" value={lifetimeBasicAll} headingLevel={1} />
    ];
};

const renderBasicChart = (state) => {
    const {
        monthlyPrincipal,
        monthlyInterest,
        monthlyTax,
        insurance,
        pmi,
        hoa,
        totalInterest,
        totalPrincipalAndInterest,
    } = calculateHomeOutputs(state);

    const monthlyTaxesAndFees = monthlyTax + insurance + pmi + hoa;

    const data = [
        { x: 'Principal', y: monthlyPrincipal },
        { x: 'Interest', y: monthlyInterest },
        { x: 'Taxes and Fees', y: monthlyTaxesAndFees },
    ];

    return <div css={{ width: '50%' }}><PieChart data={data} labelText="Monthly Expenses" /></div>;
};

const renderAdvancedInputs = (state, processInput) => {
    const { usageCost, maintenenceYearly } = state;

    return (
        <div>
            {renderBasicInputs(state, processInput)}
            <CalculatorInput
                label="Yearly Maintenence"
                id="maintenenceYearly"
                defaultValue={maintenenceYearly}
                processInput={processInput}
                level={1}
            />
            <CalculatorInput
                label="Utilities Monthly"
                id="usageCost"
                defaultValue={usageCost}
                processInput={processInput}
                level={1}
            />
        </div>
    );
};

const renderAdvancedOutputs = (state) => {
    const {
        monthlyPrincipal,
        monthlyInterest,
        monthlyTax,
        insurance,
        pmi,
        hoa,
        totalInterest,
        totalPrincipalAndInterest,
        utilitiesMonthly,
        maintenenceMonthly,
        operationalCostsMonthly,
    } = calculateHomeOutputs(state);

    const { downPayment, loanPeriod } = state;

    const monthlyMortgage = monthlyInterest + monthlyPrincipal;
    const monthlyTaxesAndFees = monthlyTax + insurance + pmi + hoa;
    const totalMonthly = monthlyMortgage + monthlyTaxesAndFees;
    const totalMonthlyAdvanced = totalMonthly + operationalCostsMonthly;

    const lifetimePrincipal = totalPrincipalAndInterest - totalInterest;
    const lifetimeTaxesAndFees = monthlyTaxesAndFees * 12 * loanPeriod;
    const lifetimeBasicAll = totalPrincipalAndInterest + downPayment + lifetimeTaxesAndFees;
    const lifetimeOperationalCosts = operationalCostsMonthly * 12 * loanPeriod;

    return [
        <CalculatorOutput label="Payments & Expenses (monthly)" key="0" headingLevel={1} />,
        <CalculatorOutput label="Mortgage Payment" key="01" headingLevel={1} indentLevel={1} />,
        <CalculatorOutput label="Principal" value={monthlyPrincipal} key="1" indentLevel={2} />,
        <CalculatorOutput label="Interest" value={monthlyInterest} key="2" indentLevel={2} />,
        <CalculatorOutput label="Total Mortgage Payments" value={monthlyMortgage} key="001" indentLevel={1} headingLevel={1} />,
        <br />,
        <CalculatorOutput label="Taxes & Fees" indentLevel={1} headingLevel={1} />,
        <CalculatorOutput label="Property Tax" value={monthlyTax} key="3" indentLevel={2} />,
        <CalculatorOutput label="Insurance" value={insurance} key="4" indentLevel={2} />,
        <CalculatorOutput label="Private Mortage Insurance" value={pmi} key="5" indentLevel={2} />,
        <CalculatorOutput label="Home Owner's Association" value={hoa} key="6" indentLevel={2} />,
        <CalculatorOutput label="Total Taxes & Fees" value={monthlyTaxesAndFees} key="1001" indentLevel={1} headingLevel={1} />,
        <br />,
        <CalculatorOutput label="Operational Costs" indentLevel={1} headingLevel={1} />,
        <CalculatorOutput label="Utilities" value={utilitiesMonthly} indentLevel={2} level={1} />,
        <CalculatorOutput label="Maintenance" value={maintenenceMonthly} indentLevel={2} level={1} />,
        <CalculatorOutput label="Total Operational Costs" value={operationalCostsMonthly} headingLevel={1} indentLevel={1} level={1} />,
        <br />,
        <CalculatorOutput label="Monthly Expenses Total" value={totalMonthlyAdvanced} headingLevel={2} level={1} />,
        <br />,
        <CalculatorOutput label="Lifetime Totals" headingLevel={1} />,
        <CalculatorOutput label="Down Payment" value={downPayment} indentLevel={1} />,
        <CalculatorOutput label="Interest" value={totalInterest} key="7" indentLevel={1} />,
        <CalculatorOutput label="Principal" value={lifetimePrincipal} key="8" indentLevel={1} />,
        <CalculatorOutput label="Taxes & Fees" value={lifetimeTaxesAndFees} indentLevel={1} />,
        <CalculatorOutput label="Operational Costs" value={lifetimeOperationalCosts} indentLevel={1} level={1} />,
        <CalculatorOutput label="Total Payments" value={lifetimeBasicAll} headingLevel={1} level={1} />
    ];
};

const renderAdvancedChart = (state) => {
    const {
        monthlyPrincipal,
        monthlyInterest,
        monthlyTax,
        insurance,
        pmi,
        hoa,
        operationalCostsMonthly,
    } = calculateHomeOutputs(state);

    const monthlyTaxesAndFees = monthlyTax + insurance + pmi + hoa;

    const data = [
        { x: 'Principal', y: monthlyPrincipal },
        { x: 'Interest', y: monthlyInterest },
        { x: 'Taxes and Fees', y: monthlyTaxesAndFees },
        { x: 'Operational Costs', y: operationalCostsMonthly },
    ];

    return <div css={{ width: '50%' }}><PieChart data={data} labelText="Monthly Expenses" /></div>;
};

const renderWyseInputs = (state, processInput) => {
    return (
        <div>
            {renderAdvancedInputs(state, processInput)}
        </div>
    );
};

const renderWyseOutputs = (state) => {
    const { opportunityCost, estimatedValue } = calculateHomeOutputs(state);

    return [
        ...renderAdvancedOutputs(state),
        <br />,
        <CalculatorOutput label="Opportunity Cost" value={opportunityCost} key="opportunityCost" headingLevel={1} level={2} />,
        <CalculatorOutput label="Estimated Home Value in 30 Years" value={estimatedValue} key="estimatedValue" headingLevel={1} level={2} />
    ];
};

const renderWyseChart = renderAdvancedChart;

export default props => (
    <CalculatorBase
        basicInputs={renderBasicInputs}
        basicOutputs={renderBasicOutputs}
        advancedInputs={renderAdvancedInputs}
        advancedOutputs={renderAdvancedOutputs}
        wyseInputs={renderWyseInputs}
        wyseOutputs={renderWyseOutputs}
        {...props}
    />
);
